
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';


export default function Home() {
  const { t, i18n } = useTranslation();
  const applyUrl = i18n.language === 'fr' ? 'https://tally.so/r/n9brL4' : 'https://tally.so/r/nWxBpQ';
  const [timeLeft, setTimeLeft] = useState({
    days: 5,
    hours: 12,
    minutes: 30,
    seconds: 45
  });

  const [menuOpen, setMenuOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [formStatus, setFormStatus] = useState('');
  const [perksFormStatus, setPerksFormStatus] = useState('');
  const [affiliateReferrals, setAffiliateReferrals] = useState(25);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('annual');

  useEffect(() => {
    setIsVisible(true);
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) {
          return { ...prev, seconds: prev.seconds - 1 };
        } else if (prev.minutes > 0) {
          return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
        } else if (prev.hours > 0) {
          return { ...prev, hours: prev.hours - 1, minutes: 59, seconds: 59 };
        } else if (prev.days > 0) {
          return { ...prev, days: prev.days - 1, hours: 23, minutes: 59, seconds: 59 };
        }
        return prev;
      });
    }, 1000);

    return () => {
      clearInterval(timer);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  // Calculate affiliate earnings based on referrals
  const calculateEarnings = (referrals: number) => {
    const monthlyPrice = 49; // Klark Early Adopter price
    const commissionRate = 0.25; // 25% commission
    return Math.round(referrals * monthlyPrice * commissionRate);
  };

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    try {
      const response = await fetch('https://readdy.ai/api/form/d3aqv7fhm68tmmd6sdh0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setFormStatus(t('contact.success'));
        (e.target as HTMLFormElement).reset();
      } else {
        setFormStatus(t('contact.error'));
      }
    } catch (error) {
      setFormStatus(t('contact.error'));
    }
  };

  const handlePerksSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    try {
      const response = await fetch('https://readdy.ai/api/form/h3mcx7qkm68tmmd6sdi0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setPerksFormStatus('Access request sent successfully!');
        (e.target as HTMLFormElement).reset();
      } else {
        setPerksFormStatus('Failed to send request. Please try again.');
      }
    } catch (error) {
      setPerksFormStatus('Failed to send request. Please try again.');
    }
  };

  const features = [
    {
      icon: 'ri-database-2-line',
      title: t('features.items.0.title'),
      description: t('features.items.0.description'),
      status: 'live'
    },
    {
      icon: 'ri-file-chart-line',
      title: t('features.items.1.title'),
      description: t('features.items.1.description'),
      status: 'live'
    },
    {
      icon: 'ri-contract-line',
      title: t('features.items.2.title'),
      description: t('features.items.2.description'),
      status: 'live'
    },
    {
      icon: 'ri-tools-line',
      title: t('features.items.3.title'),
      description: t('features.items.3.description'),
      status: 'live'
    },
    {
      icon: 'ri-shake-hands-line',
      title: t('features.items.4.title'),
      description: t('features.items.4.description'),
      status: 'live'
    },
    {
      icon: 'ri-group-line',
      title: t('features.items.5.title'),
      description: t('features.items.5.description'),
      status: 'live'
    },
    {
      icon: 'ri-goggles-line',
      title: t('features.items.6.title'),
      description: t('features.items.6.description'),
      status: 'live'
    },
    {
      icon: 'ri-brain-line',
      title: t('features.items.7.title'),
      description: t('features.items.7.description'),
      status: 'beta',
      highlight: true
    }
  ];

  const roadmapItems = t('roadmap.items', { returnObjects: true }) as Array<{
    quarter: string;
    title: string;
    description: string;
  }>;

  const roadmapItemsWithStatus = [
    { ...roadmapItems[0], status: 100, fade: 0.5 },
    { ...roadmapItems[1], status: 100, fade: 0.5 },
    { ...roadmapItems[2], status: 70, fade: 0.5 },
    { ...roadmapItems[3], status: 12, fade: 1 }
  ];

  const testimonials = t('testimonials.items', { returnObjects: true }) as Array<{
    name: string;
    role: string;
    quote: string;
    metrics: string;
  }>;

  const testimonialsWithImages = testimonials.map((testimonial, index) => ({
    ...testimonial,
    image: `${index === 0 ? '/images/emmanuel-durand.jpeg' : index === 1 ? '/images/moussa-l.jpg' : '/images/philippe-wiiv.jpeg'}`
  }));

  return (
    <div className="min-h-screen bg-white">



      <nav className={`fixed top-0 w-full z-50 transition-all duration-500 ${scrollY > 50 ? 'bg-white/90 backdrop-blur-xl border-b border-gray-200/50' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center">
              <img
                src="https://static.readdy.ai/image/4ffd0658dd3864f0f36fc6ac2c83499d/040545ec967112f54ee644785792d792.png"
                alt="Klark"
                className="h-6 sm:h-7 w-auto"
              />
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-6 lg:space-x-8">
              <a href="#features" className="text-gray-600 hover:text-black text-sm font-medium">{t('nav.product')}</a>
              <a href="#roadmap" className="text-gray-600 hover:text-black text-sm font-medium">{t('nav.roadmap')}</a>
              <a href="#testimonials" className="text-gray-600 hover:text-black text-sm font-medium">{t('nav.customers')}</a>
              <a href="#pricing" className="text-gray-600 hover:text-black text-sm font-medium">{t('nav.pricing')}</a>
              <a href="#contact" className="text-gray-600 hover:text-black text-sm font-medium">{t('nav.contact')}</a>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => changeLanguage('en')}
                  className={`px-2 py-1 text-xs font-medium rounded ${i18n.language === 'en' ? 'bg-black text-white' : 'text-gray-600 hover:text-black'}`}
                >
                  EN
                </button>
                <button
                  onClick={() => changeLanguage('fr')}
                  className={`px-2 py-1 text-xs font-medium rounded ${i18n.language === 'fr' ? 'bg-black text-white' : 'text-gray-600 hover:text-black'}`}
                >
                  FR
                </button>
              </div>
              <a href={applyUrl} className="bg-black text-white px-4 sm:px-5 py-2 rounded-full text-sm font-medium hover:bg-gray-800">
                {t('nav.tryKlark')}
              </a>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="bg-black text-white px-4 py-2 rounded-full text-sm font-medium"
              >
                {menuOpen ? '✕' : t('nav.menu')}
              </button>
            </div>
          </div>

          {/* Mobile Dropdown */}
          {menuOpen && (
            <div className="md:hidden mt-2 bg-white shadow-lg rounded-md p-4 space-y-4 transition-all duration-300">
              <a href="#features" className="block text-gray-700 text-sm font-medium">{t('nav.product')}</a>
              <a href="#roadmap" className="block text-gray-700 text-sm font-medium">{t('nav.roadmap')}</a>
              <a href="#testimonials" className="block text-gray-700 text-sm font-medium">{t('nav.customers')}</a>
              <a href="#pricing" className="block text-gray-700 text-sm font-medium">{t('nav.pricing')}</a>
              <a href="#contact" className="block text-gray-700 text-sm font-medium">{t('nav.contact')}</a>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => changeLanguage('en')}
                  className={`px-2 py-1 text-xs font-medium rounded ${i18n.language === 'en' ? 'bg-black text-white' : 'text-gray-600 hover:text-black'}`}
                >
                  EN
                </button>
                <button
                  onClick={() => changeLanguage('fr')}
                  className={`px-2 py-1 text-xs font-medium rounded ${i18n.language === 'fr' ? 'bg-black text-white' : 'text-gray-600 hover:text-black'}`}
                >
                  FR
                </button>
              </div>

              <a href={applyUrl} className="block bg-black text-white px-4 py-2 rounded-full text-sm font-medium text-center hover:bg-gray-800">
                {t('nav.tryKlark')}
              </a>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section - Apple Light Mode */}
      <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-50 to-white pt-16">
        <div className={`max-w-4xl mx-auto px-4 sm:px-6 text-center transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>

          {/* Early Adopter Badge */}
          {/*<div className="inline-flex items-center px-3 sm:px-4 py-2 rounded-full bg-black text-white text-xs sm:text-sm font-medium mb-6 sm:mb-8">
            {t('hero.earlyAdopter')}
          </div>*/}

          {/* Main Headline */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-light text-black mb-4 sm:mb-6 leading-tight mt-20 tracking-tight">
            {t('hero.headline')}<br />
            <span className="font-medium">{t('hero.headlineAccent')}</span>
          </h1>

          {/* Subheadline */}
          <p className="text-lg sm:text-xl lg:text-2xl font-light text-gray-600 mb-8 sm:mb-12 leading-relaxed max-w-3xl mx-auto px-4">
            {t('hero.subheadline')}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-12 sm:mb-16 px-4">
            <a href={applyUrl} className="bg-black text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-medium hover:bg-gray-800 transition-all duration-300 whitespace-nowrap cursor-pointer transform hover:scale-105">
              {t('hero.joinEarlyAdopters')}
            </a>
            {/* <button className="border border-gray-300 text-black px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-medium hover:border-black hover:bg-black hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer">
              {t('hero.watchDemo')}
            </button> */}
          </div>

          {/* Product Hero Image */}
          <div className="relative max-w-5xl mx-auto px-4">
            <div className="relative">
              <img
                //src="https://readdy.ai/api/search-image?query=Clean%20modern%20AI%20dashboard%20interface%20in%20Apple%20design%20style%2C%20light%20gray%20and%20white%20interface%20with%20subtle%20blue%20accents%2C%20premium%20SaaS%20application%20design%20in%20light%20theme%2C%20sophisticated%20data%20visualizations%20with%20elegant%20typography%2C%20modern%20Apple-inspired%20UI%20elements%2C%20clean%20minimalist%20aesthetic%20with%20beautiful%20shadows%20and%20gradients&width=1000&height=600&seq=lighthero1&orientation=landscape"
                src="/images/heroimage.PNG"
                alt="Klark AI Dashboard"
                className="w-full h-auto rounded-2xl sm:rounded-3xl shadow-2xl border border-gray-200"
                style={{ transform: `translateY(${scrollY * 0.1}px)` }}
              />

              {/* Floating Elements - Hidden on mobile for cleaner look */}
              <div className="hidden sm:block absolute -top-4 sm:-top-8 -left-4 sm:-left-8 bg-white/90 backdrop-blur-xl rounded-xl sm:rounded-2xl p-3 sm:p-4 shadow-2xl border border-gray-200/50">
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                  <span className="text-xs sm:text-sm font-medium text-black">{t('hero.aiActive')}</span>
                </div>
              </div>

              <div className="hidden sm:block absolute -bottom-4 sm:-bottom-8 -right-4 sm:-right-8 bg-white/90 backdrop-blur-xl rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-2xl border border-gray-200/50">
                <div className="text-xs sm:text-sm text-gray-600 mb-1">{t('hero.revenueGrowth')}</div>
                <div className="text-2xl sm:text-3xl font-light text-black">+340%</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Affiliate Income Simulator Section - New */}
      <section id="affiliate" className="py-24 sm:py-24 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="mb-12 sm:mb-16">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-light text-white mb-4 mt-8 sm:mb-6 leading-tight">
              {t('affiliation.primaryTitle')}<br />
              <span className="font-medium bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">{t('affiliation.titleAccent')}</span>
            </h2>
            <p className="text-lg sm:text-xl font-light text-gray-300 max-w-3xl mx-auto leading-relaxed">
              {t('affiliation.subtitle')}
            </p>
          </div>

          {/* Income Calculator */}
          <div className="bg-white/5 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-8 sm:p-12 border border-white/10 mb-12">
            <div className="mb-8">
              <h3 className="text-2xl sm:text-3xl font-light text-white mb-4">
                {t('affiliation.youCanEarn')}{' '}
                {i18n.language === 'fr' ? (
                  <span className="font-medium text-blue-400">{calculateEarnings(affiliateReferrals)}€</span>
                ) : (
                  <span className="font-medium text-blue-400">€{calculateEarnings(affiliateReferrals)}</span>
                )} {t('affiliation.perMonth')}
              </h3>
              <p className="text-gray-400 text-sm sm:text-base">
                {t('affiliation.description')}
              </p>
            </div>

            {/* Slider */}
            <div className="mb-8">
              <div className="flex justify-between text-sm text-gray-400 mb-4">
                <span>0</span>
                <span>100</span>
              </div>

              <div className="relative">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={affiliateReferrals}
                  onChange={(e) => setAffiliateReferrals(parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                  style={{
                    background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${affiliateReferrals}%, #374151 ${affiliateReferrals}%, #374151 100%)`
                  }}
                />
                <div
                  className="absolute top-1/2 transform -translate-y-1/2 -translate-x-1/2 w-6 h-6 bg-blue-500 rounded-full border-4 border-white shadow-lg pointer-events-none"
                  style={{ left: `${affiliateReferrals}%` }}
                >
                  <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 bg-black text-white px-3 py-1 rounded-lg text-sm whitespace-nowrap">
                    <div className="text-center">
                      <div className="font-medium">{affiliateReferrals}</div>
                      <div className="text-xs text-gray-300">{t('affiliation.referrals')}</div>
                    </div>
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-black"></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Benefits Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <i className="ri-money-euro-circle-line text-blue-400 text-xl"></i>
                </div>
                <h4 className="text-white font-medium mb-2">{t('affiliation.firstBoxTitle')}</h4>
                <p className="text-gray-400 text-sm">{t('affiliation.firstBoxDesc')}</p>
              </div>

              <div className="text-center">
                <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <i className="ri-time-line text-green-400 text-xl"></i>
                </div>
                <h4 className="text-white font-medium mb-2">{t('affiliation.secondBoxTitle')}</h4>
                <p className="text-gray-400 text-sm">{t('affiliation.secondBoxDesc')}</p>
              </div>

              <div className="text-center">
                <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <i className="ri-dashboard-line text-purple-400 text-xl"></i>
                </div>
                <h4 className="text-white font-medium mb-2">{t('affiliation.thirdBoxTitle')}</h4>
                <p className="text-gray-400 text-sm">{t('affiliation.thirdBoxDesc')}</p>
              </div>
            </div>

            <a href={applyUrl} className="inline-block bg-gradient-to-r from-blue-500 to-purple-600 text-white px-8 py-4 rounded-full text-lg font-medium hover:from-blue-600 hover:to-purple-700 transition-all duration-300 cursor-pointer transform hover:scale-105">
              {t('affiliation.affiliateButton')}
            </a>
          </div>

          {/* How it Works */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">1</span>
              </div>
              <h3 className="text-xl font-medium text-white mb-3">{t('affiliation.firstStepTitle')}</h3>
              <p className="text-gray-400">{t('affiliation.firstStepDesc')}</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">2</span>
              </div>
              <h3 className="text-xl font-medium text-white mb-3">{t('affiliation.secondStepTitle')}</h3>
              <p className="text-gray-400">{t('affiliation.secondStepDesc')}</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-white">3</span>
              </div>
              <h3 className="text-xl font-medium text-white mb-3">{t('affiliation.thirdStepTitle')}</h3>
              <p className="text-gray-400">{t('affiliation.thirdStepDesc')}</p>
            </div>
          </div>

          <p className="text-xs sm:text-sm text-gray-400 mt-6">{t('affiliation.footnote')}</p>

        </div>
      </section>

      {/* Early Adopter Pricing - Apple Light Mode */}
      <section id="pricing" className="py-16 sm:py-24 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <div className="bg-white/50 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-2xl p-8 sm:p-12 lg:p-16 border border-gray-200/50">

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light text-black mb-4 sm:mb-6">
              {t('pricing.title')}
            </h2>

            <p className="text-lg sm:text-xl font-light text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
              {t('pricing.subtitle')}
            </p>

            {/* Billing toggle */}
            <div className="flex items-center justify-center gap-3 mb-3">
              <button
                onClick={() => setBillingPeriod('monthly')}
                className={`text-sm transition-colors cursor-pointer ${billingPeriod === 'monthly' ? 'font-medium text-black' : 'text-gray-600 hover:text-black'}`}
              >
                {t('pricing.toggleMonthly')}
              </button>
              <button
                onClick={() => setBillingPeriod(billingPeriod === 'monthly' ? 'annual' : 'monthly')}
                className={`w-12 h-6 rounded-full relative transition-colors ${billingPeriod === 'annual' ? 'bg-black' : 'bg-gray-300'}`}
                aria-label="Toggle billing period"
              >
                <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 shadow transition-all duration-300 ${billingPeriod === 'annual' ? 'right-0.5' : 'left-0.5'}`}></div>
              </button>
              <button
                onClick={() => setBillingPeriod('annual')}
                className={`text-sm transition-colors cursor-pointer ${billingPeriod === 'annual' ? 'font-medium text-black' : 'text-gray-600 hover:text-black'}`}
              >
                {t('pricing.toggleAnnual')}
              </button>
            </div>
            <div className="flex justify-center mb-10 h-7">
              {billingPeriod === 'annual' && (
                <span className="inline-block bg-red-500 text-white text-xs px-3 py-1 rounded-full font-medium">
                  {t('pricing.savingsBadge')}
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 mb-8 sm:mb-12 items-stretch">
              {/* Monthly card */}
              <div className={`rounded-2xl p-6 sm:p-8 text-left border transition-all duration-300 ${billingPeriod === 'monthly' ? 'bg-white border-2 border-black shadow-2xl' : 'bg-gray-100/60 border-gray-200'}`}>
                <div className="text-xs text-gray-500 uppercase tracking-wide mb-4">{t('pricing.monthlyLabel')}</div>
                <div className="flex items-baseline mb-2">
                  <span className="text-4xl sm:text-5xl font-light text-black">{t('pricing.monthlyPrice')}</span>
                  <span className="text-sm text-gray-500 ml-2">{t('pricing.perMonthHT')}</span>
                </div>
                <p className="text-sm text-gray-600 mb-8">{t('pricing.monthlyBilled')}</p>

                <ul className="space-y-3 mb-8 text-sm">
                  {(['bankAggregation', 'crm', 'tools', 'integrations', 'partnerDiscounts', 'prioritySupport', 'updates'] as const).map((key) => (
                    <li key={key} className="flex items-start">
                      <div className="w-5 h-5 rounded-full bg-gray-800 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                        <i className="ri-check-line text-white text-xs"></i>
                      </div>
                      <span className="text-gray-700">{t(`pricing.features.${key}`)}</span>
                    </li>
                  ))}
                </ul>

                <a href={applyUrl} className={`block text-center w-full py-3 rounded-full font-medium transition-colors ${billingPeriod === 'monthly' ? 'bg-black text-white hover:bg-gray-800' : 'border border-gray-400 text-black hover:bg-gray-50'}`}>
                  {t('pricing.promote')}
                </a>
              </div>

              {/* Annual card */}
              <div className={`relative rounded-2xl text-left border transition-all duration-300 overflow-hidden ${billingPeriod === 'annual' ? 'bg-white border-2 border-black shadow-2xl' : 'bg-gray-100/60 border-gray-200'}`}>
                <div className="flex">
                  <div className="bg-emerald-300 text-white text-[11px] font-medium px-3 py-1.5 rounded-br-2xl whitespace-nowrap">
                    {t('pricing.freeTrial')}
                  </div>
                  <div className="ml-auto bg-rose-300 text-white text-[11px] font-medium px-3 py-1.5 rounded-bl-2xl whitespace-nowrap">
                    {t('pricing.earlyAccess')}
                  </div>
                </div>
                <div className="p-6 sm:p-8">
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-4">{t('pricing.annualLabel')}</div>
                  <div className="flex items-baseline mb-2">
                    <span className="text-4xl sm:text-5xl font-light text-black">{t('pricing.annualPrice')}</span>
                    <span className="text-sm text-gray-500 ml-2">{t('pricing.perMonthHT')}</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-8">{t('pricing.annualBilled')}</p>

                  <ul className="space-y-3 mb-8 text-sm">
                    <li className="flex items-start">
                      <div className="w-5 h-5 rounded-full bg-gray-800 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                        <i className="ri-check-line text-white text-xs"></i>
                      </div>
                      <span className="text-gray-700">{t('pricing.twoMonthsFree')}</span>
                    </li>
                    {(['bankAggregation', 'crm', 'tools', 'integrations', 'partnerDiscounts', 'prioritySupport', 'updates'] as const).map((key) => (
                      <li key={key} className="flex items-start">
                        <div className="w-5 h-5 rounded-full bg-gray-800 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                          <i className="ri-check-line text-white text-xs"></i>
                        </div>
                        <span className="text-gray-700">{t(`pricing.features.${key}`)}</span>
                      </li>
                    ))}
                  </ul>

                  <a href={applyUrl} className={`block text-center w-full py-3 rounded-full font-medium transition-colors ${billingPeriod === 'annual' ? 'bg-black text-white hover:bg-gray-800' : 'border border-gray-400 text-black hover:bg-gray-50'}`}>
                    {t('pricing.promote')}
                  </a>
                </div>
              </div>
            </div>

            {/* Countdown Timer - Light Mode */}
            <div className="bg-gray-100/50 backdrop-blur-xl rounded-xl sm:rounded-2xl p-6 sm:p-8 mb-6 sm:mb-8 border border-gray-200/50">
              <div className="grid grid-cols-4 gap-2 sm:gap-4 text-center">
                {Object.entries(timeLeft).map(([unit, value]) => (
                  <div key={unit}>
                    <div className="text-2xl sm:text-3xl lg:text-4xl font-light text-black mb-1 sm:mb-2">{value}</div>
                    <div className="text-xs sm:text-sm text-gray-600 uppercase tracking-wide">{t(`pricing.timeUnits.${unit}`)}</div>
                  </div>
                ))}
              </div>
            </div>

            <a href={applyUrl} className="bg-black text-white px-8 sm:px-12 py-3 sm:py-4 rounded-full text-lg sm:text-xl font-medium hover:bg-gray-800 transition-all duration-300 whitespace-nowrap cursor-pointer transform hover:scale-105">
              {t('pricing.secureSpot')}
            </a>

            <p className="text-xs sm:text-sm text-gray-600 mt-4 sm:mt-6">
              {t('pricing.savings')}
            </p>
          </div>
        </div>
      </section>

      {/* Features Section - Apple Light Grid */}
      <section id="features" className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16 sm:mb-20">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-light text-black mb-4 sm:mb-6 leading-tight">
              {t('features.title')}<br />
              <span className="font-medium">{t('features.titleAccent')}</span>
            </h2>
            <p className="text-lg sm:text-xl font-light text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {t('features.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {features.map((feature, index) => (
              <div key={index} className={`group bg-gray-50/50 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-6 sm:p-8 border transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 ${feature.highlight ? 'border-black bg-gray-100/50' : 'border-gray-200'} hover:border-gray-400`}>

                <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-6 ${feature.highlight ? 'bg-black' : 'bg-gray-200'} group-hover:scale-110 transition-transform duration-300`}>
                  <i className={`${feature.icon} text-lg sm:text-xl ${feature.highlight ? 'text-white' : 'text-gray-600'}`}></i>
                </div>

                <h3 className="text-lg sm:text-xl font-medium text-black mb-3 sm:mb-4">{feature.title}</h3>
                <p className="text-gray-600 font-light leading-relaxed text-sm sm:text-base">{feature.description}</p>

                {feature.status === 'beta' && (
                  <div className="inline-flex items-center px-3 py-1 rounded-full bg-black text-white text-xs font-medium mt-3 sm:mt-4">
                    {t('features.betaAccess')}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Perks Section - New Section with Big SaaS Brands */}
      <section className="py-16 sm:py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16 sm:mb-20">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-light text-black mb-4 sm:mb-6 leading-tight">
              {t('perks.title')}<br />
              <span className="font-medium">{t('perks.titleAccent')}</span>
            </h2>
            <p className="text-lg sm:text-xl font-light text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {t('perks.subtitle')}
            </p>
          </div>

          {/* Brand Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-12 sm:mb-16">
            {/* Notion */}
            <div className="bg-white/50 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-gray-200 hover:shadow-2xl hover:border-gray-300 transition-all duration-300 group">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300">
                  <i className="ri-notion-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-black">Notion</h3>
                  <p className="text-sm text-gray-600">Productivity workspace</p>
                </div>
              </div>
              <p className="text-gray-600 font-light text-sm">{t('perks.brands.0')}</p>
            </div>

            {/* Stripe */}
            <div className="bg-white/50 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-gray-200 hover:shadow-2xl hover:border-gray-300 transition-all duration-300 group">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-[#635BFF] rounded-xl flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300">
                  <img src="/images/stripe-logo.png" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-black">Stripe</h3>
                  <p className="text-sm text-gray-600">Payment processing</p>
                </div>
              </div>
              <p className="text-gray-600 font-light text-sm">{t('perks.brands.1')}</p>
            </div>

            {/* Shopify */}
            <div className="bg-white/50 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-gray-200 hover:shadow-2xl hover:border-gray-300 transition-all duration-300 group">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300">
                  <img src="/images/shopify-logo.png" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-black">Shopify</h3>
                  <p className="text-sm text-gray-600">E-commerce platform</p>
                </div>
              </div>
              <p className="text-gray-600 font-light text-sm">{t('perks.brands.2')}</p>
            </div>

            {/* Slack */}
            <div className="bg-white/50 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-gray-200 hover:shadow-2xl hover:border-gray-300 transition-all duration-300 group">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-[#4A154B] rounded-xl flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300">
                  <i className="ri-slack-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-black">Slack</h3>
                  <p className="text-sm text-gray-600">Team communication</p>
                </div>
              </div>
              <p className="text-gray-600 font-light text-sm">{t('perks.brands.3')}</p>
            </div>

            {/* HubSpot */}
            <div className="bg-white/50 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-gray-200 hover:shadow-2xl hover:border-gray-300 transition-all duration-300 group">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300">
                  <img src='/images/hubspot-logo.png' />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-black">HubSpot</h3>
                  <p className="text-sm text-gray-600">CRM & Marketing</p>
                </div>
              </div>
              <p className="text-gray-600 font-light text-sm">{t('perks.brands.4')}</p>
            </div>

            {/* Zapier */}
            <div className="bg-white/50 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-gray-200 hover:shadow-2xl hover:border-gray-300 transition-all duration-300 group">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300">
                  <img src="/images/zapier-logo.png" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-black">Zapier</h3>
                  <p className="text-sm text-gray-600">Workflow automation</p>
                </div>
              </div>
              <p className="text-gray-600 font-light text-sm">{t('perks.brands.5')}</p>
            </div>
          </div>

          {/* Access Request Form */}
          {/* <div className="max-w-2xl mx-auto">
            <div className="bg-white/50 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-2xl p-8 sm:p-12 border border-gray-200/50">
              <div className="text-center mb-8">
                <h3 className="text-2xl sm:text-3xl font-light text-black mb-4">
                  Request Integration Access
                </h3>
                <p className="text-gray-600 font-light">
                  Get early access to our premium integrations and unlock the full potential of your business stack.
                </p>
              </div>
              
              <form onSubmit={handlePerksSubmit} data-readdy-form id="perks_access">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="perk_name" className="block text-sm font-medium text-black mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="perk_name"
                      name="name"
                      required
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-black focus:outline-none transition-colors text-sm bg-white"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label htmlFor="perk_email" className="block text-sm font-medium text-black mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="perk_email"
                      name="email"
                      required
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-black focus:outline-none transition-colors text-sm bg-white"
                      placeholder="your.email@company.com"
                    />
                  </div>
                </div>
                
                <div className="mb-6">
                  <label htmlFor="perk_company" className="block text-sm font-medium text-black mb-2">
                    Company *
                  </label>
                  <input
                    type="text"
                    id="perk_company"
                    name="company"
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-black focus:outline-none transition-colors text-sm bg-white"
                    placeholder="Your company name"
                  />
                </div>
                
                <div className="mb-8">
                  <label htmlFor="perk_integrations" className="block text-sm font-medium text-black mb-2">
                    Integrations Needed *
                  </label>
                  <textarea
                    id="perk_integrations"
                    name="integrations"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-black focus:outline-none transition-colors text-sm bg-white resize-none"
                    placeholder="Which integrations are you most interested in? (Notion, Stripe, Shopify, Slack, HubSpot, Zapier, etc.)"
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-2">Maximum 500 characters</p>
                </div>
                
                <button
                  type="submit"
                  className="w-full bg-black text-white py-4 rounded-xl text-lg font-medium hover:bg-gray-800 transition-all duration-300 cursor-pointer transform hover:scale-[1.02]"
                >
                  {t('perks.askAccess')}
                </button>
                
                {perksFormStatus && (
                  <div className={`mt-4 p-4 rounded-xl text-center text-sm ${
                    perksFormStatus.includes('successfully') 
                      ? 'bg-green-50 text-green-800 border border-green-200' 
                      : 'bg-red-50 text-red-800 border border-red-200'
                  }`}>
                    {perksFormStatus}
                  </div>
                )}
              </form>
            </div>
          </div> */}
        </div>
      </section>

      {/* Roadmap Section - Apple Light Timeline */}
      <section id="roadmap" className="py-16 sm:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16 sm:mb-20">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-light text-black mb-4 sm:mb-6">
              {t('roadmap.title')}<br />
              <span className="font-medium">{t('roadmap.titleAccent')}</span>
            </h2>
            <p className="text-lg sm:text-xl font-light text-gray-600 max-w-3xl mx-auto">
              {t('roadmap.subtitle')}
            </p>
          </div>

          <div className="space-y-8 sm:space-y-12">
            {roadmapItemsWithStatus.map((item, index) => (
              <div
              key={index}
              style={{ opacity: item.fade }}
              >
              <div className="flex flex-col sm:flex-row items-start sm:items-center">
                <div className="flex-shrink-0 w-full sm:w-32 text-left sm:text-right mb-4 sm:mb-0 sm:mr-8">
                  <span className="text-base sm:text-lg font-medium text-black">{item.quarter}</span>
                </div>
                
                <div className="hidden sm:flex flex-shrink-0 mr-8 relative items-center justify-center w-6 h-6">
                  {item.status === 100 ? (
                    <div className="w-6 h-6 bg-black rounded-full flex items-center justify-center">
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 text-white">
                        <path d="M20 6L9 17l-5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                  ) : (
                    <div className="w-6 h-6 bg-black rounded-full"></div>
                  )}

                  {index < roadmapItemsWithStatus.length - 1 && (
                    <div className="absolute top-6 left-1/2 w-0.5 h-12 bg-gray-300 transform -translate-x-1/2"></div>
                  )}
                </div>
                
                <div className="w-full flex-1 bg-gray-50/50 backdrop-blur-xl rounded-xl sm:rounded-2xl p-6 sm:p-8 shadow-2xl border border-gray-200/50 hover:shadow-2xl hover:border-gray-300 transition-all duration-300">
                  <div className="flex items-center justify-between mb-3 sm:mb-4">
                    <h3 className="text-lg sm:text-xl font-medium text-black">{item.title}</h3>
                    <span className="text-sm text-gray-600">{item.status}%</span>
                  </div>
                  <p className="text-gray-600 font-light leading-relaxed mb-3 sm:mb-4 text-sm sm:text-base">{item.description}</p>
                  <div className="w-full bg-gray-200 rounded-full h-1">
                    <div 
                      className="bg-black h-1 rounded-full transition-all duration-1000"
                      style={{ width: `${item.status}%` }}
                    ></div>
                  </div>
                </div>
              </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials - Apple Light Style */}
      <section id="testimonials" className="py-16 sm:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16 sm:mb-20">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-light text-black mb-4 sm:mb-6">
              {t('testimonials.title')}<br />
              <span className="font-medium">{t('testimonials.titleAccent')}</span>
            </h2>
            <p className="text-lg sm:text-xl font-light text-gray-600 max-w-3xl mx-auto">
              {t('testimonials.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {testimonialsWithImages.map((testimonial, index) => (
              <div key={index} className="bg-white/50 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-gray-200 hover:shadow-2xl hover:border-gray-300 transition-all duration-300">
                <div className="flex items-center mb-4 sm:mb-6">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover mr-3 sm:mr-4"
                  />
                  <div>
                    <h4 className="font-medium text-black text-sm sm:text-base">{testimonial.name}</h4>
                    <p className="text-gray-600 text-xs sm:text-sm font-light">{testimonial.role}</p>
                  </div>
                </div>

                <p className="text-gray-700 font-light leading-relaxed mb-4 sm:mb-6 text-sm sm:text-base">{testimonial.quote}</p>

                <div className="text-sm font-medium text-black">{testimonial.metrics}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section - Apple Light Style */}
      <section id="contact" className="py-16 sm:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-light text-black mb-4 sm:mb-6 leading-tight">
              {t('contact.title')} <span className="font-medium">{t('contact.titleAccent')}</span>
            </h2>
            <p className="text-lg sm:text-xl font-light text-gray-600 max-w-2xl mx-auto">
              {t('contact.subtitle')}
            </p>
          </div>

          <div className="bg-white/50 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-2xl p-8 sm:p-12 border border-gray-200/50">
            <form onSubmit={handleContactSubmit} data-readdy-form id="contact_us">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-black mb-2">
                    {t('contact.form.name')}
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-black focus:outline-none transition-colors text-sm bg-white"
                    placeholder={t('contact.form.namePlaceholder')}
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-black mb-2">
                    {t('contact.form.email')}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-black focus:outline-none transition-colors text-sm bg-white"
                    placeholder={t('contact.form.emailPlaceholder')}
                  />
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="company" className="block text-sm font-medium text-black mb-2">
                  {t('contact.form.company')}
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-black focus:outline-none transition-colors text-sm bg-white"
                  placeholder={t('contact.form.companyPlaceholder')}
                />
              </div>

              <div className="mb-6">
                <label htmlFor="subject" className="block text-sm font-medium text-black mb-2">
                  {t('contact.form.subject')}
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  required
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-black focus:outline-none transition-colors text-sm bg-white"
                  placeholder={t('contact.form.subjectPlaceholder')}
                />
              </div>

              <div className="mb-8">
                <label htmlFor="message" className="block text-sm font-medium text-black mb-2">
                  {t('contact.form.message')}
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={6}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-black focus:outline-none transition-colors text-sm bg-white resize-none"
                  placeholder={t('contact.form.messagePlaceholder')}
                ></textarea>
                <p className="text-xs text-gray-500 mt-2">{t('contact.form.maxChars')}</p>
              </div>

              <button
                type="submit"
                className="w-full bg-black text-white py-4 rounded-xl text-lg font-medium hover:bg-gray-800 transition-all duration-300 cursor-pointer"
              >
                {t('contact.form.sendMessage')}
              </button>

              {formStatus && (
                <div className={`mt-4 p-4 rounded-xl text-center text-sm ${formStatus.includes('successfully')
                  ? 'bg-green-50 text-green-800 border border-green-200'
                  : 'bg-red-50 text-red-800 border border-red-200'
                  }`}>
                  {formStatus}
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* Final CTA - Apple Light Style */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-light text-black mb-6 sm:mb-8 leading-tight">
            {t('finalCta.title')}<br />
            <span className="font-medium">{t('finalCta.titleAccent')}</span>
          </h2>
          <p className="text-lg sm:text-xl font-light text-gray-600 mb-8 sm:mb-12 max-w-2xl mx-auto">
            {t('finalCta.subtitle')}
          </p>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-8 sm:mb-12">
            <a href={applyUrl} className="bg-black text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-medium hover:bg-gray-800 transition-all duration-300 whitespace-nowrap cursor-pointer transform hover:scale-105">
              {t('finalCta.getKlark')}
            </a>
            <a href='#roadmap' className="border border-gray-300 text-black px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-medium hover:border-black hover:bg-black hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer">
              {t('finalCta.learnMore')}
            </a>
          </div>

        </div>
      </section>

      {/* Footer - Apple Light Minimalist */}
      <footer className="bg-gray-50 py-12 sm:py-16 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-8 mb-8 sm:mb-12">
            <div className="sm:col-span-2">
              <img
                src="https://static.readdy.ai/image/4ffd0658dd3864f0f36fc6ac2c83499d/040545ec967112f54ee644785792d792.png"
                alt="Klark"
                className="h-6 sm:h-8 w-auto mb-4 sm:mb-6"
              />
              <p className="text-gray-600 font-light leading-relaxed max-w-md text-sm sm:text-base">
                {t('footer.description')}
              </p>
            </div>

            {/* <div>
              <h4 className="font-medium text-black mb-3 sm:mb-4 text-sm sm:text-base">{t('footer.sections.product.title')}</h4>
              <ul className="space-y-2 sm:space-y-3 text-xs sm:text-sm text-gray-600 font-light">
                <li><a href="#features" className="hover:text-black cursor-pointer transition-colors">Features</a></li>
                <li><a href="#roadmap" className="hover:text-black cursor-pointer transition-colors">Roadmap</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-medium text-black mb-3 sm:mb-4 text-sm sm:text-base">Company</h4>
              <ul className="space-y-2 sm:space-y-3 text-xs sm:text-sm text-gray-600 font-light">
                <li><a href="#" className="hover:text-black cursor-pointer transition-colors">About</a></li>
                <li><a href="#" className="hover:text-black cursor-pointer transition-colors">News</a></li>
                <li><a href="#" className="hover:text-black cursor-pointer transition-colors">Careers</a></li>
                <li><a href="#contact" className="hover:text-black cursor-pointer transition-colors">Contact</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-medium text-black mb-3 sm:mb-4 text-sm sm:text-base">Support</h4>
              <ul className="space-y-2 sm:space-y-3 text-xs sm:text-sm text-gray-600 font-light">
                <li><a href="#" className="hover:text-black cursor-pointer transition-colors">Help</a></li>
                <li><a href="#" className="hover:text-black cursor-pointer transition-colors">Privacy</a></li>
                <li><a href="#" className="hover:text-black cursor-pointer transition-colors">Terms</a></li>
                <li><a href="#" className="hover:text-black cursor-pointer transition-colors">Legal</a></li>
              </ul>
            </div> */}
          </div>

          <div className="border-t border-gray-200 pt-6 sm:pt-8 flex flex-col sm:flex-row justify-between items-center">
            <p className="text-gray-500 text-xs sm:text-sm font-light mb-4 sm:mb-0">
              © 2026 Klark. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
